import axios, { AxiosInstance, CancelToken } from 'axios';
import qs from 'qs';
import { ApiRequestConfig } from '../types/http';
import axiosInstant from '../config/axios';
import { SUBTITLES_API_URL, BASE_URL } from '../config/secrets';
import { CustomObject } from '../types/data';

type Cache = {
  [key: string]: {
    lastUpdate: number;
    data: any;
  };
};

const { socket_room, slideshow } = qs.parse(window.location.search, { ignoreQueryPrefix: true });

class ApiService {
  private axios: AxiosInstance;
  private cache: Cache;

  constructor(_axios: AxiosInstance) {
    this.axios = _axios;

    this.cache = {};
  }

  request = async (
    path: string,
    {
      method = 'GET',
      data,
      params,
      headers,
      cancelToken,
      version = 'v1',
      disableCache = false,
      cacheLiveTime = 60000,
    }: ApiRequestConfig = {}
  ) => {
    const url = `/${version}${path}`;

    let res;

    if (method.toUpperCase() === 'GET' && !disableCache) {
      res = this.cache[url];

      if (res) {
        const delta = Date.now() - res.lastUpdate;

        if (delta > cacheLiveTime) {
          delete this.cache[url];
        } else {
          return res.data;
        }
      }
    }

    res = await this.axios.request({
      url,
      method,
      data,
      params,
      headers,
      cancelToken,
    });

    this.cache[url] = {
      lastUpdate: Date.now(),
      data: res,
    };

    return res;
  };

  getPoint = (university: string, id: string | number, params: CustomObject = {}, cancelToken?: CancelToken) => {
    return this.request(`/point/${university}/${id}`, {
      method: 'GET',
      params: {
        room: socket_room,
        slideshow: slideshow,
        ...params,
      },
      cancelToken,
    });
  };

  getExperience = (id: string | number) => this.request(`/experiences/${id}`, { method: 'GET' });

  getLiveTourExperience = (id: string | number) => this.request(`/live/tour/${id}`, { method: 'GET', version: 'v3' });

  getSlideshow = (id: string | number) => this.request(`/slideshow/${id}`, { method: 'GET', version: 'v3' });

  getStory = (university: string, id: string | number, params: CustomObject = {}, cancelToken?: CancelToken) => {
    return this.request(`/story/${id}`, {
      method: 'GET',
      params: {
        room: socket_room,
        slideshow: slideshow,
        ...params,
      },
      cancelToken,
    });
  };

  toggleStroyAsFavorite = (id: string | number, favorite: boolean) => {
    return this.axios.request({
      baseURL: (window as any).BASE_URL + (window as any).LOCALE,
      url: `/ajax/stories/like`,
      method: 'POST',
      data: { id, action: favorite ? 'like' : 'unlike' },
    });
    // return this.request(`/stories/${favorite ? 'like' : 'unlike'}`, { method: 'POST', data: { id } });
  };

  getPlace = (id: string | number, params: CustomObject = {}, cancelToken?: CancelToken) => {
    return this.request(`/places/${id}`, {
      method: 'GET',
      params: {
        room: socket_room,
        slideshow: slideshow,
        ...params,
      },
      cancelToken,
    });
  };

  togglePlaceAsFavorite = (id: string | number, favorite: boolean) => {
    return this.axios.request({
      baseURL: (window as any).BASE_URL + (window as any).LOCALE,
      url: `/ajax/places/like`,
      method: 'POST',
      data: { id, action: favorite ? 'like' : 'unlike' },
    });
    // return this.request(`/places/favorite/${favorite ? 'add' : 'remove'}`, { method: 'POST', data: { id } });
  };

  getSubtitles = async (id: string | number) => {
    const res = await axios.request({
      baseURL: (window as any).SUBTITLES_API_URL,
      url: `/audios/v2/story/${id}.json`,
      method: 'GET',
    });
    return res;
  };
}

export default new ApiService(axiosInstant);
